import React, { useEffect, useMemo, useState } from 'react'
import { Formik, Form } from 'formik'
import { useAccountId, useTenantPartnerId } from '@labrav/common'
import { getFlyOutOnboardingData } from '../../../modules/flyOutOnboarding/action'
import { CloudType } from '../../../types'
import { useDispatch, useSelector } from '../../../store'
import {
  FlyoutOnboardingSectionName,
  LockMetadata,
  Sections,
} from '../../../modules/flyOutOnboarding/types'
import { isEmpty } from 'lodash'
import { getFlyOutValidationSchema } from '../../../common/utils/FlyoutOnboardingValidations'
import flyOutOnboardingTouched from '../../../mocks/flyOutOnboardingTouched.json'
import {
  FooterV2,
  footerStateType,
} from '../../../common/components/FooterV2/FooterV2'
import { FlyOutCompanyDetails } from '../../FlyoutCompanyDetails'
import { FlyOutCloudSettings } from '../../FlyOutCloudSettings'
import { useLocation } from 'react-router-dom'
import {
  AutoSaveFeedback,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { FlyOutOnboardingSaveButton } from './FlyOutOnboardingSaveButton/FlyOutOnboardingSaveButton'
import {
  ScrollToFieldError,
  type AutoSaveTypes,
  type UserProfileState,
} from '@labrav/common'
import { DateTime } from 'luxon'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faOctagonExclamation } from '@fortawesome/pro-regular-svg-icons'
import { Typography } from '@mui/material'
import { autoSaveThresoldTime } from '../../../common/utils/constants'
import { useStyles } from './FlyOutAwsOnboarding.styles'
import { useFlagValue } from '@labrav/flags'

const FlyOutAwsOnboarding = () => {
  const { classes } = useStyles({})
  const { pathname } = useLocation()
  const { value: isMarketplaceCloudSettingsRevampEnabled } = useFlagValue(
    'marketplaceCloudSettingsRevamp'
  )
  const { value: isOnboardingImprovementsEnabled } = useFlagValue(
    'onboardingImprovements'
  )
  const flyOutValidationSchema = useMemo(
    () =>
      getFlyOutValidationSchema(
        Boolean(isMarketplaceCloudSettingsRevampEnabled),
        Boolean(isOnboardingImprovementsEnabled)
      ),
    [isMarketplaceCloudSettingsRevampEnabled, isOnboardingImprovementsEnabled]
  )

  const [page, setPage] = useState(FlyoutOnboardingSectionName.CLOUD_SETTINGS)
  const [lockStatus, setLockStatus] = useState('')
  const [startEditTime, setStartEditTime] = useState<string | undefined>()
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)

  const accountId = useAccountId({ product: 'flyout', cloud: CloudType.AWS })
  const tenantPartnerId = useTenantPartnerId()
  const dispatch = useDispatch()

  const flyOutOnboardingForm = useSelector<Sections>(
    state => state.flyOutOnboarding.onboardingForm
  )

  const { userProfile } = useSelector<UserProfileState>(
    state => state.userProfile
  )

  const autoSaveStatus = useSelector<{ status: AutoSaveTypes; error?: string }>(
    state => state.autoSave?.flyOutOnboarding ?? { status: 'standBy' }
  )

  useEffect(() => {
    const contentEle = document.getElementById('content-id')
    if (contentEle) {
      contentEle?.scrollTo(0, 0)
    }
  }, [page])

  useEffect(() => {
    if (accountId && tenantPartnerId && isEmpty(flyOutOnboardingForm)) {
      dispatch(
        getFlyOutOnboardingData(accountId, tenantPartnerId, CloudType.AWS)
      )
    }
  }, [accountId, flyOutOnboardingForm, tenantPartnerId])
  useEffect(() => {
    if (flyOutOnboardingForm) {
      const lockMetadata =
        flyOutOnboardingForm[FlyoutOnboardingSectionName.LOCK_METADATA]
      if (lockMetadata) {
        if (
          lockMetadata.lockedBy &&
          lockMetadata.lockedBy === userProfile.id &&
          lockMetadata.windowId === window.name
        ) {
          setLockStatus('')
        }
      }
    }
  }, [flyOutOnboardingForm[FlyoutOnboardingSectionName.LOCK_METADATA]])

  useEffect(() => {
    if (lockStatus === '') {
      setStartEditTime(DateTime.now().toUTC().toISO())
    } else {
      setStartEditTime(undefined)
    }
  }, [lockStatus])

  useEffect(() => {
    if (pathname.includes('cloud-settings')) {
      setPage(FlyoutOnboardingSectionName.CLOUD_SETTINGS)
    } else if (pathname.includes('company-information')) {
      setPage(FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS)
    }
  }, [pathname])

  const noOps = () => void 0

  useEffect(() => {
    if (!isEmpty(flyOutOnboardingForm)) {
      const lockMetadata =
        flyOutOnboardingForm[FlyoutOnboardingSectionName.LOCK_METADATA]
      if (lockMetadata) {
        const timeDiffnew =
          Math.ceil(
            DateTime.now().diff(
              startEditTime ? DateTime.fromISO(startEditTime) : DateTime.now(),
              ['seconds']
            ).seconds
          ) > autoSaveThresoldTime
        if (lockMetadata.lockedBy && lockMetadata.lockedBy !== userProfile.id) {
          if (lockStatus === '' && timeDiffnew) {
            setIsOpenModal(true)
          }
          setLockStatus(footerStateType.LOCKED)
        } else {
          if (lockMetadata.windowId !== window.name) {
            if (lockStatus === '' && timeDiffnew) {
              setIsOpenModal(true)
            }
            setLockStatus(footerStateType.LOCKED)
          }
        }
      }
    }
  }, [
    flyOutOnboardingForm[FlyoutOnboardingSectionName.LOCK_METADATA]
      ?.lastActivityAt,
    flyOutOnboardingForm[FlyoutOnboardingSectionName.LOCK_METADATA],
  ])

  const lastActivityAt = DateTime.now().toUTC().toISO()

  const latestLockMetadata: LockMetadata = {
    windowId: window.name,
    lockedBy: userProfile?.id as string,
    lastActivityAt: lastActivityAt,
    lockedByName:
      `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim(),
    lockByEmail: userProfile.email,
  }

  if (isEmpty(flyOutOnboardingForm)) {
    return <></>
  }

  const isFormLocked = false //lockStatus !== '' //TODO: Remove Lock mechanism for onboarding
  const disableTooltipMsg = isFormLocked
    ? `Unable to edit as ${
        flyOutOnboardingForm[FlyoutOnboardingSectionName.LOCK_METADATA]
          .lockedByName
      } is editing the form right now.`
    : ''

  return (
    <Formik
      initialValues={flyOutOnboardingForm}
      validationSchema={flyOutValidationSchema}
      initialTouched={flyOutOnboardingTouched}
      validateOnMount={true}
      onSubmit={noOps}
      enableReinitialize
    >
      {formikProps => (
        <Form>
          <div>
            <LabraToolTipV2
              tooltipPlacement="bottom-start"
              description={
                lockStatus === footerStateType.LOCKED
                  ? disableTooltipMsg
                  : lockStatus === footerStateType.UNLOCKED
                    ? 'Click on “Start editing” below to edit the form.'
                    : ''
              }
              tooltipPlacmentGap="32px"
              followCursor={isFormLocked}
            >
              <div
                className={clsx(
                  classes.formContainer,
                  isFormLocked ? classes.formDisabled : ''
                )}
              >
                <ScrollToFieldError />
                <div className={isFormLocked ? classes.disableEvents : ''}>
                  {page ===
                    FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS && (
                    <FlyOutCompanyDetails
                      companyDetails={formikProps}
                      data-testid="flyout-welcome"
                      isFormLocked={isFormLocked}
                    />
                  )}
                  {page === FlyoutOnboardingSectionName.CLOUD_SETTINGS && (
                    <FlyOutCloudSettings
                      cloudSettings={formikProps}
                      isFormLocked={isFormLocked}
                    />
                  )}
                </div>
              </div>
            </LabraToolTipV2>
          </div>
          <FooterV2
            actions={
              <div className={classes.onboardingActions}>
                {/* {lockStatus !== '' ? (
                  <LockMessageState
                    sinceTime={
                      formikProps?.values?.[
                        FlyoutOnboardingSectionName.LOCK_METADATA
                      ]?.lastActivityAt
                    }
                    lockByName={
                      formikProps?.values?.[
                        FlyoutOnboardingSectionName.LOCK_METADATA
                      ]?.lockedByName
                    }
                    lockStatus={lockStatus}
                    setLockStatus={setLockStatus}
                    userId={userProfile.id as string}
                    lockedBy={
                      formikProps?.values?.[
                        FlyoutOnboardingSectionName.LOCK_METADATA
                      ]?.lockedBy
                    }
                    lockByEmail={
                      formikProps?.values?.[
                        FlyoutOnboardingSectionName.LOCK_METADATA
                      ]?.lockByEmail
                    }
                  />
                ) : !autoSaveStatus || autoSaveStatus.status === 'standBy' ? (
                  <></>
                ) : (
                  <div className={classes.autosave}>
                    <AutoSaveFeedback status={autoSaveStatus.status} />
                  </div>
                )} */}
                {!autoSaveStatus || autoSaveStatus.status === 'standBy' ? (
                  <div></div>
                ) : (
                  <div className={classes.autosave}>
                    <AutoSaveFeedback status={autoSaveStatus.status} />
                  </div>
                )}
                <div className={classes.btnWrapper}>
                  <FlyOutOnboardingSaveButton
                    sectionName={page}
                    isFormLocked={isFormLocked}
                    disableTooltipMsg={disableTooltipMsg}
                    lockStatus={lockStatus}
                    latestLockMetadata={latestLockMetadata}
                    userProfileId={userProfile?.id as string}
                    setLockStatus={setLockStatus}
                  />
                </div>
              </div>
            }
            // footerState={lockStatus}
            customClass={classes.root}
          />
          <ModalConfirmV2
            title={'Lock released'}
            content={
              <Typography className={classes.modalText}>
                Your editing session expired due to inactivity, and the form is
                now being edited by{' '}
                <span className={classes.nameBoldText}>
                  {flyOutOnboardingForm?.[
                    FlyoutOnboardingSectionName.LOCK_METADATA
                  ]?.lockedByName || ''}
                </span>
                . Check back later.
              </Typography>
            }
            open={isOpenModal}
            titleAcceptButton={'Ok'}
            onAccept={() => setIsOpenModal(false)}
            onDecline={() => setIsOpenModal(false)}
            variant={ModalSizeVariants.small}
            acceptDisabled={false}
            hideCancelButton={true}
            icon={
              <FontAwesomeIcon
                icon={faOctagonExclamation}
                style={{ width: '20px', height: '16px' }}
              />
            }
          />
        </Form>
      )}
    </Formik>
  )
}

export { FlyOutAwsOnboarding }
export default FlyOutAwsOnboarding
